/**
 * NinjaCreditsService — Angular service for Ninja Coins economy
 *
 * Provides Observable-based access to:
 * - Credit ledger summary (balance, cycle dates, thresholds)
 * - Credit transaction log (paginated)
 * - Credit pack subscription: Stripe checkout, change size, cancel/resume
 * - Reactive credit balance stream for UI binding
 *
 * All API calls require a valid Firebase Auth ID token (handled by HttpClient
 * interceptor via AuthInterceptor / standard auth header injection).
 *
 * Usage:
 * ```typescript
 * // In a component:
 * readonly credits$ = this.ninjaCreditsService.creditSummary$;
 * ```
 *
 * @module core/services/ninja-credits.service
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  from,
  switchMap,
  catchError,
  of,
  tap,
  interval,
  startWith,
  throwError,
  EMPTY,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { Auth } from '@angular/fire/auth';
import { LoggingService } from './logging.service';
import { ApiConfigurationService } from './api-configuration.service';
import { FirebaseInjectionHelperService } from './firebase-injection-helper.service';
import {
  NinjaCreditTopupPack,
  NINJA_CREDIT_TOPUP_PACKS,
  NINJA_CREDIT_COSTS,
  NinjaCreditAction,
} from './tier-access.service';

// ---------------------------------------------------------------------------
// Response interfaces matching the API
// ---------------------------------------------------------------------------

/** The org's recurring credit pack as reported by the API. */
export interface CreditPackSubscriptionSummary {
  stripeSubscriptionId: string;
  packId: string;
  packName: string;
  /** Credits granted each pack billing month */
  monthlyCredits: number;
  priceCents: number;
  status:
    | 'active'
    | 'trialing'
    | 'past_due'
    | 'unpaid'
    | 'incomplete'
    | 'incomplete_expired'
    | 'paused'
    | 'canceled';
  /** True once the customer asked to stop at the end of the paid period */
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  /** Pack that applies from the next renewal after a downgrade, or null */
  pendingPackId?: string | null;
  pendingPackName?: string | null;
  pendingMonthlyCredits?: number | null;
}

export interface CreditSummary {
  planCredits: number;
  rolloverCredits: number;
  /** Credits from the recurring pack for its current period (plus any legacy one-time top-ups) */
  topupCredits: number;
  totalAvailable: number;
  monthlyAllotment: number;
  overdraftUsed: number;
  overdraftAllowance: number;
  cycleStartDate: string | null;
  cycleEndDate: string | null;
  softAlertThreshold: number;
  hardAlertThreshold: number;
  isAnnualPlan: boolean;
  /** The org's recurring credit pack, or null when none is held */
  packSubscription?: CreditPackSubscriptionSummary | null;
  /** Credits the pack grants each month; 0 when no pack is active */
  packMonthlyCredits?: number;
}

export interface CreditTransaction {
  id: string;
  timestamp: string;
  userId: string;
  userEmail?: string;
  action: string;
  creditsConsumed: number;
  creditsAvailableBefore: number;
  creditsAvailableAfter: number;
  success: boolean;
  waived: boolean;
  waiverReason?: string;
  metadata?: Record<string, string | number | boolean>;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

interface TopupCheckoutResponse {
  success: boolean;
  sessionId: string;
  url: string;
}

interface PackChangeResponse {
  success: boolean;
  data?: {
    packId: string;
    monthlyCredits: number;
    effect: 'upgrade_now' | 'downgrade_next_renewal' | 'pending_change_cancelled';
  };
  error?: string;
}

const POLL_INTERVAL_MS = 5 * 60_000; // Refresh balance every 5 minutes (background polling)

// ---------------------------------------------------------------------------
// NinjaCreditsService
// ---------------------------------------------------------------------------

@Injectable({
  providedIn: 'root',
})
export class NinjaCreditsService {
  private http = inject(HttpClient);
  private auth = inject(Auth);
  private loggingService = inject(LoggingService);
  private apiConfigService = inject(ApiConfigurationService);
  private firebaseHelper = inject(FirebaseInjectionHelperService);

  private readonly apiBase = environment.app_api_url;

  /** BehaviorSubject backing the reactive credit summary stream */
  private _creditSummary$ = new BehaviorSubject<CreditSummary | null>(null);

  /**
   * Observable of the current credit ledger summary.
   * Emits on:
   * - Initial subscription
   * - Explicit refresh call (refreshCreditBalance)
   * - Background poll every 60 seconds
   *
   * Emits null before first successful fetch.
   */
  readonly creditSummary$: Observable<CreditSummary | null> = this._creditSummary$.asObservable();

  /** True when a fetch is in progress */
  private _loading$ = new BehaviorSubject<boolean>(false);
  readonly loading$ = this._loading$.asObservable();

  /** Expose the credit pack catalogue (client-side constant, no API needed). Prices are per month. */
  readonly topupPacks: readonly NinjaCreditTopupPack[] = NINJA_CREDIT_TOPUP_PACKS;

  /** Credit costs map for template reference */
  readonly creditCosts: Readonly<Record<NinjaCreditAction, number>> = NINJA_CREDIT_COSTS;

  constructor() {
    // Firebase restores a persisted session asynchronously, so `auth.currentUser`
    // is null for a short window after bootstrap even when the user is signed in.
    // Fetching before that window closes fails auth and leaves the balance null
    // until the next poll — five minutes of empty credit UI. Gate the first fetch
    // (and each poll) on the restored auth state instead.
    from(this.auth.authStateReady())
      .pipe(
        switchMap(() => interval(POLL_INTERVAL_MS).pipe(startWith(0))),
        catchError((err) => {
          this.loggingService.logError(
            'NinjaCreditsService: auth state never became ready',
            err,
          );
          return EMPTY;
        }),
      )
      .subscribe(() => this.refreshCreditBalance());

    // Sign-in (or a company/user switch) must not wait out the poll interval.
    //
    // `onAuthStateChanged` fires immediately with the *current* user, so its
    // first emission describes the same restored session the chain above has
    // already fetched for. Acting on it would send a second
    // /api/credits/summary request on every page load. Only transitions after
    // that first emission are new information.
    let authStateSettled = false;
    this.firebaseHelper.executeOnAuthStateChanged((user: unknown) => {
      if (!authStateSettled) {
        authStateSettled = true;
        return;
      }
      if (user) {
        this.refreshCreditBalance();
      } else {
        this._creditSummary$.next(null);
      }
    });
  }

  // -------------------------------------------------------------------------
  // Ledger reads
  // -------------------------------------------------------------------------

  /**
   * Fetch the current credit ledger summary from the API and push it to
   * creditSummary$. Safe to call multiple times — debouncing is not required
   * as the API is light (single Firestore read with 5s in-memory cache).
   */
  refreshCreditBalance(): void {
    this._loading$.next(true);
    this.getAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.get<ApiResponse<CreditSummary>>(
          `${this.apiBase}/api/credits/summary`,
          { headers }
        )
      ),
      tap((response) => {
        if (response.success && response.data) {
          this._creditSummary$.next(response.data);
        }
      }),
      catchError((err) => {
        this.loggingService.logError('NinjaCreditsService: Failed to fetch credit summary', err);
        return of(null);
      }),
      tap(() => this._loading$.next(false))
    ).subscribe();
  }

  /**
   * Get Ninja Coin ledger summary as a one-shot Observable.
   * Prefer using creditSummary$ for reactive UI binding.
   */
  getCreditSummary(): Observable<CreditSummary | null> {
    return this.getAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.get<ApiResponse<CreditSummary>>(
          `${this.apiBase}/api/credits/summary`,
          { headers }
        )
      ),
      switchMap((response) => (response.success && response.data) ? of(response.data) : of(null)),
      catchError((err) => {
        this.loggingService.logError('NinjaCreditsService: getCreditSummary error', err);
        return of(null);
      })
    );
  }

  /**
   * Get the paginated credit transaction log.
   *
   * @param limit - Number of records per page (default 50, max 200)
   * @param startAfter - Pagination cursor (transaction doc ID)
   */
  getCreditTransactions(
    limit = 50,
    startAfter?: string
  ): Observable<{ transactions: CreditTransaction[]; nextPageToken: string | null }> {
    const params: Record<string, string> = { limit: String(limit) };
    if (startAfter) params['startAfter'] = startAfter;

    const queryString = new URLSearchParams(params).toString();

    return this.getAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.get<ApiResponse<CreditTransaction[]> & { nextPageToken: string | null }>(
          `${this.apiBase}/api/credits/transactions?${queryString}`,
          { headers }
        )
      ),
      switchMap((response) =>
        of({
          transactions: response.data ?? [],
          nextPageToken: response.nextPageToken ?? null,
        })
      ),
      catchError((err) => {
        this.loggingService.logError('NinjaCreditsService: getCreditTransactions error', err);
        return of({ transactions: [], nextPageToken: null });
      })
    );
  }

  // -------------------------------------------------------------------------
  // Credit pack subscription
  // -------------------------------------------------------------------------

  /**
   * Start a Stripe subscription checkout for a monthly credit pack.
   * On success, redirects the browser to the Stripe-hosted checkout page.
   * On failure, returns an error message. The API answers 409 with a message
   * when the org already holds a pack; use changePackSubscription instead.
   *
   * @param packId - ID from NINJA_CREDIT_TOPUP_PACKS
   * @param successUrl - URL to redirect to after successful checkout
   * @param cancelUrl - URL to redirect to if checkout is abandoned
   * @returns Observable<string | null> — null on success (redirected), error string on failure
   */
  initiatePackCheckout(
    packId: string,
    successUrl: string,
    cancelUrl: string
  ): Observable<string | null> {
    return this.getAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.post<TopupCheckoutResponse>(
          `${this.apiBase}/api/credits/pack/checkout`,
          { packId, successUrl, cancelUrl },
          { headers }
        )
      ),
      tap((response) => {
        if (response.success && response.url) {
          window.location.href = response.url;
        }
      }),
      switchMap(() => of(null)),
      catchError((err) => {
        if (err?.error?.code === 'PACK_CHECKOUT_PENDING' && err?.error?.url) {
          // Another checkout for this org is still open (a second tab, or a
          // double-click). Resume it rather than failing.
          window.location.href = err.error.url;
          return of(null);
        }
        this.loggingService.logError('NinjaCreditsService: initiatePackCheckout error', err);
        const message = err?.error?.error ?? 'Failed to initiate checkout. Please try again.';
        return of(message as string);
      })
    );
  }

  /** @deprecated Use initiatePackCheckout. Packs are monthly subscriptions, not one-time top-ups. */
  initiateTopupCheckout(
    packId: string,
    successUrl: string,
    cancelUrl: string
  ): Observable<string | null> {
    return this.initiatePackCheckout(packId, successUrl, cancelUrl);
  }

  /**
   * Switch the org's pack to another size. An upgrade is prorated and its
   * extra credits land through the webhook, so the balance is refreshed after
   * a short delay as well as at once. A downgrade is scheduled for the next
   * renewal; choosing the current pack again cancels the scheduled change.
   *
   * @returns Observable<string | null> — null on success, error string on failure
   */
  changePackSubscription(packId: string): Observable<string | null> {
    return this.getAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.post<PackChangeResponse>(
          `${this.apiBase}/api/credits/pack/change`,
          { packId },
          { headers }
        )
      ),
      tap(() => {
        this.refreshCreditBalance();
        setTimeout(() => this.refreshCreditBalance(), 4_000);
      }),
      switchMap(() => of(null)),
      catchError((err) => {
        this.loggingService.logError('NinjaCreditsService: changePackSubscription error', err);
        const message = err?.error?.error ?? 'Failed to change the credit pack. Please try again.';
        return of(message as string);
      })
    );
  }

  /**
   * Cancel the org's pack at the end of the paid period, or resume one that
   * was scheduled to cancel. Publishes the summary the API returns.
   *
   * @returns Observable<string | null> — null on success, error string on failure
   */
  cancelPackSubscription(resume = false): Observable<string | null> {
    return this.getAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.post<ApiResponse<CreditSummary>>(
          `${this.apiBase}/api/credits/pack/cancel`,
          { resume },
          { headers }
        )
      ),
      tap((response) => {
        if (response.success && response.data) {
          this._creditSummary$.next(response.data);
        }
      }),
      switchMap(() => of(null)),
      catchError((err) => {
        this.loggingService.logError('NinjaCreditsService: cancelPackSubscription error', err);
        const message = err?.error?.error ?? 'Failed to update the credit pack. Please try again.';
        return of(message as string);
      })
    );
  }

  // -------------------------------------------------------------------------
  // Derived helpers for templates
  // -------------------------------------------------------------------------

  /**
   * Compute the burn rate as a percentage (0–100) of monthly allotment used.
   * Returns 0 if allotment is 0.
   */
  getBurnPercent(summary: CreditSummary): number {
    if (summary.monthlyAllotment <= 0) return 0;
    const used = summary.monthlyAllotment - summary.planCredits;
    return Math.min(100, Math.round((used / summary.monthlyAllotment) * 100));
  }

  /**
   * Returns the Bootstrap 5 contextual color class for the credit balance bar.
   * - 0–79%: 'success'
   * - 80–94%: 'warning'
   * - 95–100%: 'danger'
   */
  getBalanceColorClass(summary: CreditSummary): 'success' | 'warning' | 'danger' {
    const pct = this.getBurnPercent(summary);
    if (pct >= summary.hardAlertThreshold) return 'danger';
    if (pct >= summary.softAlertThreshold) return 'warning';
    return 'success';
  }

  /**
   * Format credits with commas for display (e.g. 1500 → "1,500").
   */
  formatCredits(n: number): string {
    return new Intl.NumberFormat('en-US').format(n);
  }

  // -------------------------------------------------------------------------
  // Auth header helper
  // -------------------------------------------------------------------------

  /**
   * Get Authorization headers with current Firebase ID token and API function key.
   * Refreshes the token if expired.
   *
   * Waits on `auth.authStateReady()` first: Firebase restores a persisted session
   * asynchronously, so reading `currentUser` synchronously right after bootstrap
   * reports "signed out" for a user who is in fact signed in.
   *
   * Errors (rather than completing empty) when no user is signed in or the API
   * function token cannot be resolved, so the failure reaches each caller's
   * `catchError` and their loading flags get cleared. Returning `EMPTY` here
   * would complete without emitting: `switchMap` would never run, `catchError`
   * would never fire, and subscribers would receive neither `next` nor `error`
   * — leaving spinners running forever.
   *
   * @returns Observable emitting the headers once, or erroring if they cannot be built.
   */
  private getAuthHeaders(): Observable<HttpHeaders> {
    return from(this.auth.authStateReady()).pipe(
      switchMap(() => {
        const user = this.auth.currentUser;
        if (!user) {
          this.loggingService.logError('NinjaCreditsService: No authenticated user', {});
          return throwError(
            () => new Error('NinjaCreditsService: no authenticated user'),
          );
        }
        return from(user.getIdToken());
      }),
      switchMap((firebaseToken) =>
        from(this.apiConfigService.getApiFunctionToken()).pipe(
          switchMap((apiFunctionToken) => {
            const headers: Record<string, string> = {
              Authorization: `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json',
              'x-functions-key': apiFunctionToken.value,
            };

            return of(new HttpHeaders(headers));
          })
        )
      ),
      catchError((err) => {
        this.loggingService.logError('NinjaCreditsService: Failed to get auth headers', err);
        return throwError(() => err);
      })
    );
  }
}
